import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import { createReader, type NodeRelease, type NodeVersion } from '../lib/data';
import { useNodeRelease } from '../lib/mutations';
import {
  activeRollout, automaticIdleUpdatesOn, bundleUrl, computerRolloutSettled, computersForRollout,
  describeRunningNodeBuild, planComputerRollout, parseBundledNodeRelease, type BundledNodeRelease,
} from '../lib/node-updates';

interface RolloutInput {
  client: SupabaseClient;
  guard: Guard;
  orgId: string;
  versions: readonly NodeVersion[];
  releases: readonly NodeRelease[];
  running: readonly { readonly node_name: string }[];
  onChanged: () => Promise<void>;
}

export interface ComputerRollout {
  readonly bundle: BundledNodeRelease | null;
  readonly manifestError: string | null;
  /** The organization switch, or null until it has been read. The server runs automatic idle updates. */
  readonly automatic: boolean | null;
  readonly setAutomatic: (next: boolean) => void;
  readonly updateAll: () => void;
  readonly clearTarget: (nodeId: string) => void;
  readonly busy: boolean;
  readonly error: string | null;
}

/** Update all, while Command is open. Automatic idle updates are the server's, under the organization switch. */
export function useComputerRollout({ client, guard, orgId, versions, releases, running, onChanged }: RolloutInput): ComputerRollout {
  const { publish, target, setAutomaticWhenIdle, busy, error } = useNodeRelease(client);
  const [bundle, setBundle] = useState<BundledNodeRelease | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [automatic, setAutomaticState] = useState<boolean | null>(null);
  const [manual, setManual] = useState(false);
  const [kick, setKick] = useState(0);
  const suppressed = useRef(new Set<string>());
  const lastAttempt = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    void fetch('/node/releases/latest.json', { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error('unavailable');
        const parsed = parseBundledNodeRelease(await response.json());
        if (parsed === null) throw new Error('invalid');
        if (live) { setBundle(parsed); setManifestError(null); }
      })
      .catch(() => { if (live) setManifestError('The verified node release manifest is not available yet.'); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    void createReader(client).nodeUpdateSettings(orgId).then((result) => {
      if (live && result.ok) setAutomaticState(automaticIdleUpdatesOn(result.rows));
    });
    return () => { live = false; };
  }, [client, orgId]);

  const computers = useMemo(() => computersForRollout(versions, running), [versions, running]);
  const plan = planComputerRollout({
    bundle, releases, computers, mode: manual ? 'manual' : 'off', suppressedNodeIds: [...suppressed.current],
  });

  useEffect(() => {
    if (bundle !== null && manual && computerRolloutSettled(bundle, computers, [...suppressed.current])) {
      setManual(false);
    }
  }, [bundle, computers, manual]);

  const attemptKey = `${kick}|${bundle?.version ?? ''}|${plan.publish ? 'publish' : ''}|${plan.targetNodeId ?? ''}`;
  useEffect(() => {
    if (bundle === null || (!plan.publish && plan.targetNodeId === null)) return;
    if (lastAttempt.current === attemptKey) return;
    lastAttempt.current = attemptKey;
    const current = bundle;
    const nodeId = plan.targetNodeId;
    const doPublish = plan.publish;
    void guard('post_owner_command', async () => {
      if (doPublish) {
        const ok = await publish({
          orgId,
          version: current.version,
          bundleUrl: bundleUrl(window.location.origin, current.bundlePath),
          bundleSha256: current.bundleSha256,
          notes: `Verified from commit ${current.commit.slice(0, 12)}`,
        });
        if (ok) await onChanged();
        return;
      }
      if (nodeId !== null) {
        const ok = await target(orgId, nodeId, current.version);
        if (ok) await onChanged();
      }
    });
  }, [attemptKey, bundle, plan.publish, plan.targetNodeId, guard, publish, target, orgId, onChanged]);

  const setAutomatic = useCallback((next: boolean) => {
    void guard('post_owner_command', async () => {
      if (await setAutomaticWhenIdle(orgId, next)) setAutomaticState(next);
    });
  }, [guard, setAutomaticWhenIdle, orgId]);

  const updateAll = useCallback(() => {
    suppressed.current.clear();
    setManual(true);
    setKick((value) => value + 1);
  }, []);

  const clearTarget = useCallback((nodeId: string) => {
    suppressed.current.add(nodeId);
    setKick((value) => value + 1);
    void guard('post_owner_command', async () => {
      if (await target(orgId, nodeId, null)) await onChanged();
    });
  }, [guard, target, orgId, onChanged]);

  return { bundle, manifestError, automatic, setAutomatic, updateAll, clearTarget, busy, error };
}

/** Settings Updates. The room passes its rollout so Update all keeps going while Settings is closed. */
export function NodeUpdates(props: Omit<RolloutInput, 'running'> & {
  running?: readonly { readonly node_name: string }[];
  rollout?: ComputerRollout;
}) {
  const running = props.running ?? [];
  if (props.rollout) return <NodeUpdatesView versions={props.versions} releases={props.releases} running={running} rollout={props.rollout} />;
  return <OwnRollout {...props} running={running} />;
}

function OwnRollout(props: RolloutInput) {
  const rollout = useComputerRollout(props);
  return <NodeUpdatesView versions={props.versions} releases={props.releases} running={props.running} rollout={rollout} />;
}

function NodeUpdatesView({ versions, releases, running, rollout }: {
  versions: readonly NodeVersion[];
  releases: readonly NodeRelease[];
  running: readonly { readonly node_name: string }[];
  rollout: ComputerRollout;
}) {
  const { bundle, manifestError, automatic, busy, error } = rollout;
  const published = useMemo(() => bundle === null ? null : releases.find((row) =>
    row.version === bundle.version && row.bundle_sha256 === bundle.bundleSha256) ?? null, [bundle, releases]);
  const rolling = activeRollout(versions);
  const computers = useMemo(() => computersForRollout(versions, running), [versions, running]);

  return (
    <div className="stack">
      <small className="muted">
        Verified builds roll out one computer at a time. The next computer stays locked until the current one reports the target version or its target is cleared. A computer with a working task is left alone until that task is finished.
      </small>
      {bundle === null ? <p className="muted">{manifestError ?? 'Reading the latest verified node build…'}</p> : (
        <div className="row spread">
          <span><strong>{bundle.version}</strong><small className="muted"> · commit {bundle.commit.slice(0, 12)} · checksum {bundle.bundleSha256.slice(0, 12)}…</small></span>
          {published !== null && <span className="tag">published</span>}
        </div>
      )}
      <div className="row">
        <button type="button" className="primary" disabled={busy || bundle === null} onClick={rollout.updateAll}>Update all computers</button>
      </div>
      <label className="row" title="Brunel checks for a newer verified build and updates an idle computer, even while Command is closed.">
        <input type="checkbox" checked={automatic ?? true} disabled={busy || automatic === null}
          onChange={(event) => rollout.setAutomatic(event.target.checked)} />
        Update computers automatically when idle
      </label>
      <ul className="list">
        {versions.map((node) => {
          const current = bundle !== null && node.running_version === bundle.version;
          const targeted = bundle !== null && node.target_version === bundle.version;
          const workingTask = computers.some((computer) => computer.nodeId === node.node_id && computer.workingTask);
          const build = describeRunningNodeBuild({
            name: node.name,
            runningVersion: node.running_version,
            releases,
            bundle,
          });
          return (
            <li key={node.node_id} className="stack">
              <div className="row spread">
                <strong>{node.name}</strong>
                <span className="muted">{build.running}{node.target_version ? ` · target ${node.target_version}` : ''}</span>
              </div>
              <small className={build.kind === 'merged_not_live' ? undefined : 'muted'}>{build.relation}</small>
              {node.update_state && (
                <small className="muted">
                  Last update: {node.update_state}{node.update_version ? ` · ${node.update_version}` : ''}{node.update_detail ? ` · ${node.update_detail}` : ''}
                </small>
              )}
              <div className="row">
                {current ? <span className="tag">current</span> : targeted ? (
                  <>
                    <span className="tag">{node.behind ? 'updating' : 'targeted'}</span>
                    <button type="button" className="link" disabled={busy} onClick={() => rollout.clearTarget(node.node_id)}>Clear target</button>
                  </>
                ) : workingTask && bundle !== null ? (
                  <small className="muted">Waiting until this computer is idle.</small>
                ) : rolling !== null && rolling.node_id !== node.node_id ? (
                  <small className="muted">{rolling.name} must finish its rollout first.</small>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {versions.length === 0 && <p className="muted">No computers have reported a node version yet.</p>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
